import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import { notifyError } from '../notify';


// Auto-saving input — saves on blur or after 1s of inactivity
function AutoSaveInput({ value, onSave, placeholder, disabled, multiline, ...props }) {
  const [localValue, setLocalValue] = useState(value || '');
  const timerRef = useRef(null);
  const savedRef = useRef(value || '');

  const save = useCallback((val) => {
    if (val !== savedRef.current) {
      savedRef.current = val;
      onSave(val);
    }
  }, [onSave]);

  const handleChange = (e) => {
    setLocalValue(e.target.value);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => save(e.target.value), 1000);
  };

  const handleBlur = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    save(localValue);
  };

  // Sync if parent value changes
  useEffect(() => {
    setLocalValue(value || '');
    savedRef.current = value || '';
  }, [value]);

  if (multiline) {
    return <textarea value={localValue} onChange={handleChange} onBlur={handleBlur} placeholder={placeholder} disabled={disabled} rows={3} style={{ width: '100%' }} {...props} />;
  }
  return <input value={localValue} onChange={handleChange} onBlur={handleBlur} placeholder={placeholder} disabled={disabled} {...props} />;
}

function PostingLists({ creatorAccountIds, role }) {
  const [lists, setLists] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [subreddits, setSubreddits] = useState([]);
  const [tags, setTags] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [expandedList, setExpandedList] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [editingListId, setEditingListId] = useState(null);
  const [editingListName, setEditingListName] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', account_id: '' });

  // Subreddit picker modal
  const [pickerModal, setPickerModal] = useState(null); // { listId }
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerTags, setPickerTags] = useState([]);
  const [pickerSort, setPickerSort] = useState('az');
  const [pickerPerPage, setPickerPerPage] = useState(20);
  const [pickerPage, setPickerPage] = useState(1);

  // Subreddit detail modal
  const [detailSub, setDetailSub] = useState(null);

  // Bulk add & picker tabs
  const [pickerTab, setPickerTab] = useState('all'); // 'all' | 'added' | 'bulk'
  const [bulkAddText, setBulkAddText] = useState('');
  const [bulkAddLoading, setBulkAddLoading] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  // Creator switched → the previously picked account belongs to someone else
  useEffect(() => {
    setSelectedAccount('all');
    setExpandedList(null);
  }, [creatorAccountIds]);

  const fetchAll = async () => {
    try {
      const [listsRes, accountsRes, tagsRes] = await Promise.all([
        axios.get(`${API}/api/posting-lists`),
        axios.get(`${API}/api/accounts`),
        axios.get(`${API}/api/tags`)
      ]);
      setLists(listsRes.data);
      setAccounts(accountsRes.data);
      setTags(tagsRes.data);
    } catch (err) {
      notifyError(err, 'Could not load posting lists');
    }
  };

  const createList = async () => {
    if (!form.name || !form.account_id) return;
    setLoading(true);
    try {
      await axios.post(`${API}/api/posting-lists`, form);
      setForm({ name: '', account_id: '' });
      setShowForm(false);
      fetchAll();
    } catch (err) {
      notifyError(err, 'Could not create list');
    }
    setLoading(false);
  };

  const deleteList = async (id) => {
    if (!window.confirm('Delete this posting list?')) return;
    try {
      await axios.delete(`${API}/api/posting-lists/${id}`);
      fetchAll();
    } catch (err) {
      notifyError(err, 'Could not delete list');
    }
  };

  const addSubreddit = async (listId, subredditId) => {
    // Optimistic: update UI instantly
    const sub = subreddits.find(s => s.id === subredditId);
    const tempEntry = { id: `temp-${Date.now()}`, subreddit_id: subredditId, posting_list_id: listId, subreddits: sub };
    setLists(prev => prev.map(l => {
      if (l.id !== listId) return l;
      return { ...l, posting_list_subreddits: [...(l.posting_list_subreddits || []), tempEntry] };
    }));
    try {
      const res = await axios.post(`${API}/api/posting-lists/${listId}/subreddits`, { subreddit_id: subredditId });
      // Replace temp entry with real data
      setLists(prev => prev.map(l => {
        if (l.id !== listId) return l;
        return { ...l, posting_list_subreddits: (l.posting_list_subreddits || []).map(s => s.id === tempEntry.id ? res.data : s) };
      }));
    } catch (err) {
      // Revert on failure
      setLists(prev => prev.map(l => {
        if (l.id !== listId) return l;
        return { ...l, posting_list_subreddits: (l.posting_list_subreddits || []).filter(s => s.id !== tempEntry.id) };
      }));
      notifyError(err, 'Could not add subreddit');
    }
  };

  const removeSubreddit = async (listId, subredditId) => {
    // Optimistic: remove from UI instantly
    const prevLists = lists;
    setLists(prev => prev.map(l => {
      if (l.id !== listId) return l;
      return { ...l, posting_list_subreddits: (l.posting_list_subreddits || []).filter(s => s.subreddit_id !== subredditId) };
    }));
    try {
      await axios.delete(`${API}/api/posting-lists/${listId}/subreddits/${subredditId}`);
    } catch (err) {
      // Revert on failure
      setLists(prevLists);
      notifyError(err, 'Could not remove subreddit');
    }
  };

  const updateSubredditField = async (subId, field, value) => {
    try {
      await axios.put(`${API}/api/subreddits/${subId}`, { [field]: value });
      setSubreddits(prev => prev.map(s => s.id === subId ? { ...s, [field]: value } : s));
      if (detailSub?.id === subId) setDetailSub(prev => ({ ...prev, [field]: value }));
    } catch (err) {
      notifyError(err, 'Could not save subreddit');
    }
  };

  const toggleSubredditTag = async (subId, tagName) => {
    const sub = subreddits.find(s => s.id === subId);
    const current = sub?.tags || [];
    const updated = current.includes(tagName) ? current.filter(t => t !== tagName) : [...current, tagName];
    await updateSubredditField(subId, 'tags', updated);
  };

  const bulkAddSubreddits = async (listId) => {
    const names = bulkAddText
      .split(/[\n,]+/)
      .map(n => n.trim())
      .filter(n => n.length > 0);
    if (names.length === 0) return;

    setBulkAddLoading(true);
    try {
      // 1. Ensure all subreddits exist in DB (creates missing, skips existing)
      const ensureRes = await axios.post(`${API}/api/subreddits/bulk-ensure`, { names });
      const allSubs = ensureRes.data.subreddits;

      // Update local subreddits list with any new ones
      setSubreddits(prev => {
        const existingIds = new Set(prev.map(s => s.id));
        const newSubs = allSubs.filter(s => !existingIds.has(s.id));
        return [...prev, ...newSubs].sort((a, b) => a.name.localeCompare(b.name));
      });

      // 2. Bulk add to posting list (endpoint skips duplicates)
      const subIds = allSubs.map(s => s.id);
      const bulkRes = await axios.post(`${API}/api/posting-lists/${listId}/subreddits/bulk`, { subreddit_ids: subIds });

      // 3. Update local list state with new entries
      if (bulkRes.data.entries && bulkRes.data.entries.length > 0) {
        setLists(prev => prev.map(l => {
          if (l.id !== listId) return l;
          return { ...l, posting_list_subreddits: [...(l.posting_list_subreddits || []), ...bulkRes.data.entries] };
        }));
      }

      setBulkAddText('');
      setPickerTab('added');
      const msg = `✅ ${bulkRes.data.added} added` +
        (bulkRes.data.skipped > 0 ? `, ${bulkRes.data.skipped} already in list` : '') +
        (ensureRes.data.created > 0 ? `, ${ensureRes.data.created} new subreddits created` : '');
      alert(msg);
    } catch (err) {
      alert('Error: ' + (err.response?.data?.error?.message || err.response?.data?.error || err.message));
    }
    setBulkAddLoading(false);
  };

  const getAccountName = (id) => {
    const account = accounts.find(a => a.id === id);
    return account ? account.name : 'Unknown';
  };

  const getListSubredditIds = (list) => {
    return (list.posting_list_subreddits || []).map(s => s.subreddit_id);
  };

  // Picker filtering and sorting
  const pickerFiltered = subreddits
    .filter(s => {
      const matchSearch = s.name.toLowerCase().includes(pickerSearch.toLowerCase());
      const matchTags = pickerTags.length === 0 || pickerTags.every(t => (s.tags || []).includes(t));
      return matchSearch && matchTags;
    })
    .sort((a, b) => {
      if (pickerSort === 'az') return a.name.localeCompare(b.name);
      if (pickerSort === 'za') return b.name.localeCompare(a.name);
      if (pickerSort === 'visitors_desc') return (b.weekly_visitors_count || 0) - (a.weekly_visitors_count || 0);
      if (pickerSort === 'visitors_asc') return (a.weekly_visitors_count || 0) - (b.weekly_visitors_count || 0);
      return 0;
    });

  const filteredLists = lists.filter(l => {
    const matchAccount = selectedAccount === 'all' || l.account_id === selectedAccount;
    const matchCreator = creatorAccountIds ? creatorAccountIds.includes(l.account_id) : true;
    return matchAccount && matchCreator;
  });

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Posting Lists</h1>
          <p>Create and manage your posting lists here.</p>
        </div>
        {role !== 'poster_va' && (
          <button onClick={() => {
            setForm({ name: '', account_id: selectedAccount === 'all' ? '' : selectedAccount });
            setShowForm(!showForm);
          }}>
            {showForm ? 'Cancel' : '+ Create List'}
          </button>
        )}
      </div>

      {/* Account filter */}
      <div className="card">
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setSelectedAccount('all')}
            style={{
              padding: '6px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
              background: selectedAccount === 'all' ? '#ff4500' : '#2a2a2a',
              color: selectedAccount === 'all' ? '#ffffff' : '#aaaaaa'
            }}
          >All Accounts</button>
          {accounts.filter(a => creatorAccountIds ? creatorAccountIds.includes(a.id) : true).map(account => (
            <button
              key={account.id}
              onClick={() => setSelectedAccount(account.id)}
              style={{
                padding: '6px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                background: selectedAccount === account.id ? '#ff4500' : '#2a2a2a',
                color: selectedAccount === account.id ? '#ffffff' : '#aaaaaa'
              }}
            >{account.name}</button>
          ))}
        </div>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="card">
          <h2>Create Posting List</h2>
          <div className="form-grid">
            <input
              placeholder="List name (e.g. Monday Posts)"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
            />
            {!form.account_id && (
              <select
                value={form.account_id}
                onChange={e => setForm({ ...form, account_id: e.target.value })}
              >
                <option value="">Select account first using buttons above</option>
                {accounts.filter(a => creatorAccountIds ? creatorAccountIds.includes(a.id) : true).map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name} (u/{account.reddit_username})
                  </option>
                ))}
              </select>
            )}
            {form.account_id && (
              <div style={{ fontSize: '13px', color: '#aaaaaa', padding: '8px 0' }}>
                Account: <strong style={{ color: '#ffffff' }}>{accounts.find(a => a.id === form.account_id)?.name}</strong>
              </div>
            )}
          </div>
          <div className="form-actions">
            <button onClick={createList} disabled={loading}>
              {loading ? 'Creating...' : 'Create List'}
            </button>
          </div>
        </div>
      )}

      {/* Lists */}
      {filteredLists.length === 0 ? (
        <div className="card">
          <p className="empty">No posting lists yet. Create one above.</p>
        </div>
      ) : (
        filteredLists.map(list => (
          <div className="card" key={list.id}>
            <div className="list-header">
              <div>
                {editingListId === list.id ? (
                  <div className="form-row">
                    <input
                      value={editingListName}
                      onChange={e => setEditingListName(e.target.value)}
                      style={{ width: '200px' }}
                    />
                    <button onClick={async () => {
                      try {
                        await axios.put(`${API}/api/posting-lists/${list.id}`, { name: editingListName });
                        setEditingListId(null);
                        fetchAll();
                      } catch (err) { notifyError(err, 'Could not rename list'); }
                    }}>Save</button>
                    <button className="btn-secondary" onClick={() => setEditingListId(null)}>Cancel</button>
                  </div>
                ) : (
                  <strong>{list.name}</strong>
                )}
                <span className="sub"> — {getAccountName(list.account_id)}</span>
                <span className="sub"> · {(list.posting_list_subreddits || []).length} subreddits</span>
              </div>
              <div className="list-item-actions">
                {role !== 'poster_va' && (
                  <button className="btn-secondary" onClick={() => { setEditingListId(list.id); setEditingListName(list.name); }}>Rename</button>
                )}
                <button
                  className="btn-secondary"
                  onClick={() => setExpandedList(expandedList === list.id ? null : list.id)}
                >
                  {expandedList === list.id ? 'Collapse' : 'View'}
                </button>
                {role !== 'poster_va' && (
                  <button
                    onClick={async () => {
                      setPickerModal({ listId: list.id });
                      setPickerSearch('');
                      setPickerTags([]);
                      setPickerSort('az');
                      setPickerPage(1);
                      setPickerTab('all');
                      setBulkAddText('');
                      if (subreddits.length === 0) {
                        try {
                          const res = await axios.get(`${API}/api/subreddits/names`);
                          setSubreddits(res.data);
                        } catch (err) { notifyError(err, 'Could not load subreddits'); }
                      }
                    }}
                  >
                    Manage Subreddits
                  </button>
                )}
                {role !== 'poster_va' && (
                  <button className="btn-danger" onClick={() => deleteList(list.id)}>Delete</button>
                )}
              </div>
            </div>

            {expandedList === list.id && (
              <div style={{ marginTop: '12px' }}>
                {(list.posting_list_subreddits || []).length === 0 ? (
                  <p className="empty">No subreddits added yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {(list.posting_list_subreddits || []).map(item => (
                      <span key={item.id} style={{
                        background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '20px',
                        padding: '4px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px'
                      }}>
                        <a
                          href={`https://www.reddit.com/${item.subreddits?.name || ''}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#ff4500', textDecoration: 'none' }}
                        >
                          {item.subreddits?.name}
                        </a>
                        {role !== 'poster_va' && (
                          <span
                            onClick={() => removeSubreddit(list.id, item.subreddit_id)}
                            style={{ cursor: 'pointer', color: '#ff4500', fontWeight: '700' }}
                          >×</span>
                        )}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))
      )}

      {/* Subreddit Picker Modal */}
      {pickerModal && (() => {
        const list = lists.find(l => l.id === pickerModal.listId);
        if (!list) return null;
        const listSubIds = getListSubredditIds(list);

        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', zIndex: 1001,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <div style={{
              background: '#1a1a1a', borderRadius: '12px', padding: '24px',
              width: '90%', maxWidth: '800px', maxHeight: '90vh',
              display: 'flex', flexDirection: 'column'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <h2 style={{ margin: 0 }}>Manage Subreddits</h2>
                  <span style={{ fontSize: '12px', color: '#aaaaaa' }}>{list.name} — {listSubIds.length} selected</span>
                </div>
                <button onClick={() => setPickerModal(null)} style={{ background: 'transparent', border: 'none', color: '#aaaaaa', fontSize: '20px', cursor: 'pointer' }}>×</button>
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', gap: '0', marginBottom: '16px', borderBottom: '2px solid #2a2a2a' }}>
                {[
                  { key: 'all', label: 'All Subreddits', count: pickerFiltered.length },
                  { key: 'added', label: 'Added', count: listSubIds.length },
                  { key: 'bulk', label: 'Bulk Add' }
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => { setPickerTab(tab.key); setPickerPage(1); }}
                    style={{
                      padding: '10px 20px', fontSize: '13px', fontWeight: '600',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: pickerTab === tab.key ? '#ff4500' : '#aaaaaa',
                      borderBottom: pickerTab === tab.key ? '2px solid #ff4500' : '2px solid transparent',
                      marginBottom: '-2px', transition: 'all 0.15s'
                    }}
                  >
                    {tab.label}{tab.count !== undefined ? ` (${tab.count})` : ''}
                  </button>
                ))}
              </div>

              {/* Tab: All / Added */}
              {(pickerTab === 'all' || pickerTab === 'added') && (
                <>
                  {/* Controls */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
                    <input
                      placeholder="Search subreddits..."
                      value={pickerSearch}
                      onChange={e => { setPickerSearch(e.target.value); setPickerPage(1); }}
                      autoFocus={pickerTab === 'all'}
                      style={{ flex: 1, minWidth: '150px', fontSize: '13px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #333', background: '#0f0f0f', color: '#fff' }}
                    />
                    <select
                      value={pickerSort}
                      onChange={e => { setPickerSort(e.target.value); setPickerPage(1); }}
                      style={{ fontSize: '12px', padding: '8px', background: '#0f0f0f', color: '#aaaaaa', border: '1px solid #333', borderRadius: '8px' }}
                    >
                      <option value="az">A → Z</option>
                      <option value="za">Z → A</option>
                      <option value="visitors_desc">Visitors ↓</option>
                      <option value="visitors_asc">Visitors ↑</option>
                    </select>
                    <select
                      value={pickerPerPage}
                      onChange={e => { setPickerPerPage(parseInt(e.target.value)); setPickerPage(1); }}
                      style={{ fontSize: '12px', padding: '8px', background: '#0f0f0f', color: '#aaaaaa', border: '1px solid #333', borderRadius: '8px' }}
                    >
                      {[10, 20, 30, 50, 100].map(n => (
                        <option key={n} value={n}>{n} / page</option>
                      ))}
                    </select>
                  </div>

                  {/* Tag filters */}
                  {tags.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                      {tags.map(tag => {
                        const active = pickerTags.includes(tag.name);
                        return (
                          <span key={tag.id} onClick={() => {
                            setPickerTags(prev => active ? prev.filter(t => t !== tag.name) : [...prev, tag.name]);
                            setPickerPage(1);
                          }} style={{
                            padding: '4px 12px', borderRadius: '20px', fontSize: '11px', cursor: 'pointer',
                            background: active ? '#ff4500' : '#2a2a2a',
                            color: active ? '#fff' : '#aaaaaa',
                            border: active ? '1px solid #ff4500' : '1px solid #444'
                          }}>{tag.name}</span>
                        );
                      })}
                    </div>
                  )}

                  {/* Subreddit list */}
                  <div style={{ overflowY: 'auto', flex: 1, borderRadius: '8px', border: '1px solid #2a2a2a' }}>
                    {(() => {
                      const displayItems = pickerTab === 'added'
                        ? pickerFiltered.filter(s => listSubIds.includes(s.id))
                        : pickerFiltered;
                      const totalPages = Math.ceil(displayItems.length / pickerPerPage);
                      const paginated = displayItems.slice((pickerPage - 1) * pickerPerPage, pickerPage * pickerPerPage);

                      if (paginated.length === 0) {
                        return <p style={{ color: '#555', textAlign: 'center', padding: '24px' }}>
                          {pickerTab === 'added' ? 'No subreddits added to this list yet' : 'No subreddits found'}
                        </p>;
                      }

                      return (
                        <>
                          {paginated.map(sub => {
                            const isInList = listSubIds.includes(sub.id);
                            return (
                              <div key={sub.id} style={{
                                padding: '10px 16px', borderBottom: '1px solid #2a2a2a',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                background: isInList ? '#1a0a00' : 'transparent'
                              }}>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <a
                                      href={`https://www.reddit.com/${sub.name}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{ fontSize: '14px', fontWeight: '600', color: isInList ? '#ff4500' : '#ffffff', textDecoration: 'none' }}
                                      onMouseEnter={e => e.target.style.textDecoration = 'underline'}
                                      onMouseLeave={e => e.target.style.textDecoration = 'none'}
                                    >
                                      {sub.name}
                                    </a>
                                    {sub.weekly_visitors && <span style={{ fontSize: '11px', color: '#555' }}>👀 {sub.weekly_visitors} weekly</span>}
                                  </div>
                                  {(sub.tags || []).length > 0 && (
                                    <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
                                      {(sub.tags || []).map(tag => (
                                        <span key={tag} style={{ fontSize: '10px', background: '#2a2a2a', color: '#aaaaaa', padding: '2px 8px', borderRadius: '10px' }}>{tag}</span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
                                  <button
                                    onClick={() => {
                                      const freshSub = subreddits.find(s => s.id === sub.id) || sub;
                                      setDetailSub(freshSub);
                                    }}
                                    style={{
                                      background: 'transparent', border: '1px solid #444', color: '#aaaaaa',
                                      borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px'
                                    }}
                                  >ℹ️</button>
                                  <button
                                    onClick={() => isInList
                                      ? removeSubreddit(pickerModal.listId, sub.id)
                                      : addSubreddit(pickerModal.listId, sub.id)
                                    }
                                    style={{
                                      padding: '6px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                                      fontSize: '12px', fontWeight: '600',
                                      background: isInList ? '#ef4444' : '#22c55e',
                                      color: '#ffffff'
                                    }}
                                  >
                                    {isInList ? 'Remove' : '+ Add'}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                          {totalPages > 1 && (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '12px' }}>
                              <button className="btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }} disabled={pickerPage === 1} onClick={() => setPickerPage(p => p - 1)}>← Prev</button>
                              <span style={{ color: '#aaaaaa', fontSize: '12px' }}>{pickerPage} of {totalPages}</span>
                              <button className="btn-secondary" style={{ fontSize: '12px', padding: '4px 12px' }} disabled={pickerPage === totalPages} onClick={() => setPickerPage(p => p + 1)}>Next →</button>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </>
              )}

              {/* Tab: Bulk Add */}
              {pickerTab === 'bulk' && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <div style={{ fontSize: '13px', color: '#aaaaaa', marginBottom: '12px', lineHeight: '1.6' }}>
                    Paste subreddit names below — one per line or comma-separated.<br />
                    New subreddits are automatically created in the database. Existing ones won't be overwritten.
                  </div>
                  <textarea
                    value={bulkAddText}
                    onChange={e => setBulkAddText(e.target.value)}
                    placeholder={"r/funny\nr/pics\nr/aww\n\nor: funny, pics, aww"}
                    autoFocus
                    style={{
                      flex: 1, minHeight: '200px', fontSize: '14px', padding: '14px',
                      background: '#0f0f0f', color: '#fff', border: '1px solid #333',
                      borderRadius: '8px', resize: 'vertical', fontFamily: 'monospace',
                      lineHeight: '1.8'
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                    <span style={{ fontSize: '13px', color: '#aaaaaa' }}>
                      {bulkAddText.split(/[\n,]+/).map(n => n.trim()).filter(n => n.length > 0).length} subreddits detected
                    </span>
                    <button
                      onClick={() => bulkAddSubreddits(pickerModal.listId)}
                      disabled={bulkAddLoading || !bulkAddText.trim()}
                      style={{
                        background: '#22c55e', color: '#fff', border: 'none',
                        borderRadius: '8px', padding: '10px 24px', cursor: 'pointer',
                        fontSize: '14px', fontWeight: '600',
                        opacity: bulkAddLoading || !bulkAddText.trim() ? 0.5 : 1
                      }}
                    >
                      {bulkAddLoading ? '⏳ Adding...' : `+ Add All`}
                    </button>
                  </div>
                </div>
              )}

              <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setPickerModal(null)}>Done</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Subreddit Detail Modal */}
      {detailSub && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.85)', zIndex: 1002,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setDetailSub(null)}>
          <div style={{
            background: '#1a1a1a', borderRadius: '12px', padding: '24px',
            width: '90%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <a
                  href={`https://www.reddit.com/${detailSub.name}`}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: '18px', fontWeight: '700', color: '#ff4500', textDecoration: 'none' }}
                >
                  {detailSub.name} ↗
                </a>
                {detailSub.weekly_visitors && (
                  <div style={{ fontSize: '12px', color: '#555', marginTop: '2px' }}>👀 {detailSub.weekly_visitors} weekly visitors</div>
                )}
              </div>
              <button onClick={() => setDetailSub(null)} style={{ background: 'transparent', border: 'none', color: '#aaaaaa', fontSize: '20px', cursor: 'pointer' }}>×</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {/* Tags */}
              <div>
                <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '6px' }}>TAGS</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {tags.map(tag => {
                    const active = (detailSub.tags || []).includes(tag.name);
                    return (
                      <span
                        key={tag.id}
                        onClick={() => role !== 'poster_va' && toggleSubredditTag(detailSub.id, tag.name)}
                        style={{
                          padding: '4px 12px', borderRadius: '20px', fontSize: '11px',
                          cursor: role === 'poster_va' ? 'default' : 'pointer',
                          background: active ? '#ff4500' : '#2a2a2a',
                          color: active ? '#fff' : '#aaaaaa',
                          border: active ? '1px solid #ff4500' : '1px solid #444'
                        }}
                      >{tag.name}</span>
                    );
                  })}
                </div>
              </div>

              {/* Size & Interval */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>MEMBERS</label>
                  <AutoSaveInput
                    value={detailSub.size || ''}
                    onSave={val => updateSubredditField(detailSub.id, 'size', parseInt(val) || 0)}
                    placeholder="e.g. 1500000"
                    disabled={role === 'poster_va'}
                    type="number"
                    min="0"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>POSTING INTERVAL (hours)</label>
                  <AutoSaveInput
                    value={detailSub.posting_interval_hours || ''}
                    onSave={val => updateSubredditField(detailSub.id, 'posting_interval_hours', parseInt(val) || 0)}
                    placeholder="0"
                    disabled={role === 'poster_va'}
                    type="number"
                    min="0"
                  />
                </div>
              </div>

              {/* Title requirements */}
              <div>
                <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>📝 TITLE REQUIREMENTS</label>
                <AutoSaveInput
                  value={detailSub.title_requirements || ''}
                  onSave={val => updateSubredditField(detailSub.id, 'title_requirements', val)}
                  placeholder="e.g. must include username"
                  disabled={role === 'poster_va'}
                />
              </div>

              {/* Moderation warning */}
              <div>
                <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>🗣️ MODERATION WARNING</label>
                <AutoSaveInput
                  value={detailSub.moderation_warning || ''}
                  onSave={val => updateSubredditField(detailSub.id, 'moderation_warning', val)}
                  placeholder="e.g. strict manual review"
                  disabled={role === 'poster_va'}
                />
              </div>

              {/* Bot warning */}
              <div>
                <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>🤖 BOT WARNING</label>
                <AutoSaveInput
                  value={detailSub.bot_warning || ''}
                  onSave={val => updateSubredditField(detailSub.id, 'bot_warning', val)}
                  placeholder="e.g. auto-removes links"
                  disabled={role === 'poster_va'}
                />
              </div>

              {/* Rules */}
              <div>
                <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>📋 RULES</label>
                <AutoSaveInput
                  value={detailSub.rules || ''}
                  onSave={val => updateSubredditField(detailSub.id, 'rules', val)}
                  placeholder="Subreddit rules..."
                  disabled={role === 'poster_va'}
                  multiline
                />
              </div>

              {/* General info */}
              <div>
                <label style={{ fontSize: '11px', color: '#aaaaaa', display: 'block', marginBottom: '4px' }}>ℹ️ GENERAL INFO</label>
                <AutoSaveInput
                  value={detailSub.general_info || ''}
                  onSave={val => updateSubredditField(detailSub.id, 'general_info', val)}
                  placeholder="What is this subreddit about..."
                  disabled={role === 'poster_va'}
                  multiline
                />
              </div>
            </div>

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" onClick={() => setDetailSub(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PostingLists;